<script lang="ts">
import { defineAsyncComponent, shallowReactive, type Component } from 'vue'

/*
  Preloader components mapper,
  you can provide your own key using definePageMeta on any page

  definePageMeta({
    preloaderType: 'your-key',
  })
*/
const loaders: Record<string, () => Promise<{ default: Component }>> = {
  // default and fallback
  default: () => import('~/components/loader/PageLoaderEventList.vue'),
  // for strapi static pages
  strapi: () => import('~/components/loader/PageLoaderStrapi.vue'),
  // for event details
  eventDetail: () => import('~/components/loader/PageLoaderEventDetail.vue'),
  // for magazine article details
  magazine: () => import('~/components/loader/PageLoaderMagazine.vue'),
  // for magazine home page
  magazineHome: () => import('~/components/loader/PageLoaderMagazineHome.vue'),
  // homepage
  home: () => import('~/components/loader/PageLoaderHome.vue'),
}

// Skeletons only show on client-side navigation, so they stay out of the
// initial page load (KFT-4323). Built once per app, not per PageLoader
// instance, so a layout change doesn't create and warm them again.
const loadedTypes = shallowReactive(new Set<string>())
const loadSkeleton = (type: string) =>
  loaders[type]!().then((module) => {
    loadedTypes.add(type)
    return module.default
  })

const skeletons: Record<string, Component> = Object.fromEntries(
  Object.keys(loaders).map((type) => [
    type,
    defineAsyncComponent(() => loadSkeleton(type)),
  ])
)

// Warmed once per visit when the page is idle, so the first navigation
// usually finds its skeleton ready.
let isWarmed = false
const warmSkeletons = () => {
  if (isWarmed) return
  isWarmed = true
  for (const type of Object.keys(loaders)) {
    loadSkeleton(type).catch((error) => {
      console.warn('Failed to prefetch page loader skeleton', error)
    })
  }
}
</script>

<script setup lang="ts">
const nuxtApp = useNuxtApp()
const router = useRouter()
const loading = ref<boolean>(false)

onNuxtReady(warmSkeletons)

const loadingType = ref<string>('default')
const skeletonType = computed(() =>
  loadingType.value in skeletons ? loadingType.value : 'default'
)
// Until its chunk has arrived the skeleton renders nothing, and a visible
// overlay would be a blank white page. Keep it hidden (the current page stays
// visible and clickable) until the skeleton can actually show.
const isSkeletonLoaded = computed(() => loadedTypes.has(skeletonType.value))

nuxtApp.hook('page:start', (): void => {
  loading.value = true
  const preloaderType = router?.currentRoute?.value?.meta?.preloaderType
  loadingType.value = preloaderType ? String(preloaderType) : 'default'
})

nuxtApp.hook('page:finish', (): void => {
  loading.value = false
})
</script>

<template>
  <Transition name="fade">
    <div
      v-if="loading"
      :class="{ invisible: !isSkeletonLoaded }"
      class="preloader-wrapper fixed left-0 top-0 h-full flex justify-center w-full bg-white overflow-hidden"
    >
      <div class="pt-[138px] max-w-(--breakpoint-xl) mx-auto px-4 w-full">
        <component :is="skeletons[skeletonType]" />
      </div>
    </div>
  </Transition>
</template>

<style lang="css" scoped>
.preloader-wrapper {
  z-index: 19;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
  will-change: opacity;
  opacity: 1;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
